// Keyboard shortcuts for inline text formatting inside plain <textarea> email editors.
//
// Cmd/Ctrl+B wraps the current selection in Markdown bold markers (**...**),
// Cmd/Ctrl+I wraps it in italic markers (_..._). Pressing the same shortcut again
// on an already-wrapped selection removes the markers (toggle).
//
// The markers are converted to <strong>/<em> at send time by convertToHtml()
// in EmailFooterGenerator.js. We deliberately use Markdown here (instead of raw
// HTML tags) so the text stays readable while editing.

const MARKERS = {
  b: '**',
  i: '_',
};

const restoreSelection = (el, start, end) => {
  // React re-renders on state update and resets the caret, so re-apply the
  // selection on the next frame once the new value is in the DOM.
  requestAnimationFrame(() => {
    try {
      el.focus();
      el.setSelectionRange(start, end);
    } catch (_) {
      /* element may have unmounted */
    }
  });
};

/**
 * onKeyDown handler for textareas. Returns true if the event was handled.
 * @param {KeyboardEvent} e - the React keyboard event
 * @param {(value: string) => void} setValue - setter that updates the textarea value in state
 */
export const handleFormattingKeyDown = (e, setValue) => {
  if (!(e.metaKey || e.ctrlKey) || e.altKey) return false;

  const marker = MARKERS[e.key.toLowerCase()];
  if (!marker) return false;

  const el = e.target;
  if (!el || typeof el.selectionStart !== 'number') return false;

  e.preventDefault();

  const value = el.value;
  const start = el.selectionStart;
  const end = el.selectionEnd;
  const mLen = marker.length;
  const selected = value.slice(start, end);

  // Toggle off: selection already contains the markers on both ends.
  if (
    selected.length >= 2 * mLen &&
    selected.startsWith(marker) &&
    selected.endsWith(marker)
  ) {
    const inner = selected.slice(mLen, selected.length - mLen);
    setValue(value.slice(0, start) + inner + value.slice(end));
    restoreSelection(el, start, start + inner.length);
    return true;
  }

  // Toggle off: markers sit immediately outside the selection.
  if (
    value.slice(start - mLen, start) === marker &&
    value.slice(end, end + mLen) === marker
  ) {
    setValue(value.slice(0, start - mLen) + selected + value.slice(end + mLen));
    restoreSelection(el, start - mLen, end - mLen);
    return true;
  }

  // Wrap the selection (or insert empty markers at the caret).
  setValue(value.slice(0, start) + marker + selected + marker + value.slice(end));
  restoreSelection(el, start + mLen, end + mLen);
  return true;
};
